// Viren's per-company suggestions panel — shared between the founder
// Proof Engine and Market Missions pages. One brief, two consumers.
//
// Cold-start state is a first-class UI: when Meera has not captured
// enough about the company, we say so honestly instead of hallucinating
// generic suggestions. Every render shows the founder which inputs
// grounded the brief, so they can sanity-check Viren and tell us when
// he is wrong.
import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, AlertCircle, ArrowRight } from "lucide-react";
import ProgressLoader from "./progress-loader";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

export interface ProofSuggestion {
  title: string;
  whatItProves: string;
  readinessComponentKey: string | null;
  whyItMattersForYou: string;
}

export interface MissionSuggestion {
  title: string;
  whatWeWillRun: string;
  successLooksLike: string;
  whyNow: string;
}

interface BriefResponse {
  companyName: string;
  status: "ready" | "cold_start" | "failed" | "pending";
  companyExpertise: string | null;
  proofSuggestions: ProofSuggestion[];
  missionSuggestions: MissionSuggestion[];
  inputsUsed: string | null;
  generatedAt: string | null;
  failureReason: string | null;
}

interface Props {
  kind: "proof" | "mission";
  onUseProof?: (s: ProofSuggestion) => void;
  onRequestMission?: (s: MissionSuggestion) => Promise<void> | void;
}

const PROOF_STAGES = [
  "Reading Meera's intel on your company",
  "Looking at your sector and stage",
  "Checking your readiness gaps",
  "Picking proofs that move the needle",
];
const MISSION_STAGES = [
  "Reading Meera's intel on your company",
  "Looking at your sector and stage",
  "Checking your readiness gaps",
  "Designing missions tailored to you",
];

function formatWhen(iso: string | null) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return null; }
}

export default function VirenSuggestionsPanel({ kind, onUseProof, onRequestMission }: Props) {
  const [data, setData] = useState<BriefResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionDone, setActionDone] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  async function load(force = false) {
    setErr(null);
    // Delay flipping the loader on for cached fast-paths (typical <100ms
    // GET when fingerprint matches). Without this delay, the
    // ProgressLoader flashes for a fraction of a second on every page
    // load, which reads as "something is wrong".
    let loaderTimer: ReturnType<typeof setTimeout> | null = null;
    if (force) {
      setRegenBusy(true);
    } else {
      loaderTimer = setTimeout(() => setLoading(true), 350);
    }
    try {
      const r = await fetch(`${BASE}api/founder-portal/viren/brief${force ? "/regenerate" : ""}`, {
        method: force ? "POST" : "GET",
        headers: auth(),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j?.error || "Could not load Viren's suggestions.");
        return;
      }
      const d = (await r.json()) as BriefResponse;
      setData(d);
    } finally {
      if (loaderTimer) clearTimeout(loaderTimer);
      setLoading(false);
      setRegenBusy(false);
    }
  }
  useEffect(() => { load(false); }, []);

  const stages = kind === "proof" ? PROOF_STAGES : MISSION_STAGES;
  const title = kind === "proof"
    ? `Viren is reading your company to suggest proof${data?.companyName ? ` for ${data.companyName}` : ""}`
    : `Viren is designing missions${data?.companyName ? ` for ${data.companyName}` : ""}`;

  if ((loading && !data) || regenBusy) {
    return (
      <div className="mb-5">
        <ProgressLoader active title={title} stages={stages} stageIntervalMs={3000} />
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="mb-5 rounded-xl border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-red-300">{err}</p>
          <button onClick={() => load(false)} className="text-xs text-red-200 hover:text-red-100 underline mt-1">Try again</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const heading = kind === "proof"
    ? `Viren's suggested proofs for ${data.companyName}`
    : `Viren's suggested missions for ${data.companyName}`;

  // Cold start. Honest empty-state — do not invent generic suggestions.
  if (data.status === "cold_start") {
    return (
      <div className="mb-5 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#FF5C00]" />
          <h3 className="text-sm font-semibold text-white">{heading}</h3>
        </div>
        <p className="text-xs text-white/65 leading-relaxed">
          {data.inputsUsed || "Once Meera completes your company intake, Viren will tailor suggestions to your business."}
        </p>
      </div>
    );
  }

  if (data.status === "failed") {
    return (
      <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-amber-200">{heading}</h3>
        </div>
        <p className="text-xs text-amber-200/80 mb-2">
          Viren could not generate suggestions just now. {data.failureReason ? `Reason: ${data.failureReason}.` : ""}
        </p>
        <button onClick={() => load(true)} disabled={regenBusy} className="text-xs text-amber-200 hover:text-amber-100 underline">Try again</button>
      </div>
    );
  }

  // Ready.
  const items = kind === "proof" ? data.proofSuggestions : data.missionSuggestions;
  const generatedWhen = formatWhen(data.generatedAt);

  return (
    <div className="mb-5 rounded-xl border border-[#FF5C00]/20 bg-gradient-to-b from-[#FF5C00]/[0.06] to-transparent p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-[#FF5C00] text-white flex items-center justify-center text-xs font-bold shrink-0">V</div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold truncate text-white">{heading}</h3>
            <p className="text-[10px] text-white/60">Senior Banking Lead</p>
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={regenBusy}
          data-testid={`button-viren-regenerate-${kind}`}
          className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1 shrink-0"
          title="Regenerate based on the latest data"
        ><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      {data.companyExpertise && (
        <p className="text-xs text-white/75 leading-relaxed mb-4 italic border-l-2 border-[#FF5C00]/40 pl-3">
          "{data.companyExpertise}"
        </p>
      )}

      {items.length === 0 ? (
        <p className="text-xs text-white/60">Viren did not generate any specific suggestions this round. Try refresh.</p>
      ) : (
        <div className="space-y-2.5">
          {kind === "proof" && (data.proofSuggestions).map((p, i) => {
            const key = `proof-${i}-${p.title}`;
            const done = actionDone.has(key);
            return (
              <div key={key} className="rounded-lg bg-white/[0.04] border border-white/[0.08] p-3.5">
                <h4 className="text-sm font-semibold mb-1 text-white">{p.title}</h4>
                <p className="text-[11px] text-white/60 mb-1.5">Proves: {p.whatItProves}</p>
                <p className="text-xs text-white/70 mb-2.5">Why it matters for you: {p.whyItMattersForYou}</p>
                {done ? (
                  <span className="text-[11px] text-green-300">Loaded into the form below.</span>
                ) : (
                  <button
                    onClick={() => { onUseProof?.(p); setActionDone(s => new Set(s).add(key)); }}
                    data-testid={`button-use-proof-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs text-[#FF5C00] hover:text-[#FF5C00]/80 font-medium"
                  >Use this <ArrowRight className="w-3 h-3" /></button>
                )}
              </div>
            );
          })}
          {kind === "mission" && (data.missionSuggestions).map((m, i) => {
            const key = `mission-${i}-${m.title}`;
            const busy = actionBusy === key;
            const done = actionDone.has(key);
            return (
              <div key={key} className="rounded-lg bg-white/[0.04] border border-white/[0.08] p-3.5">
                <h4 className="text-sm font-semibold mb-1 text-white">{m.title}</h4>
                <p className="text-xs text-white/70 mb-1.5">{m.whatWeWillRun}</p>
                <p className="text-[11px] text-white/60 mb-1"><span className="font-medium text-white/65">Success looks like:</span> {m.successLooksLike}</p>
                <p className="text-[11px] text-white/60 mb-2.5"><span className="font-medium text-white/65">Why now:</span> {m.whyNow}</p>
                {done ? (
                  <span className="text-[11px] text-green-300">Sent to your Advisor.</span>
                ) : (
                  <button
                    onClick={async () => {
                      if (!onRequestMission) return;
                      setActionBusy(key);
                      try { await onRequestMission(m); setActionDone(s => new Set(s).add(key)); }
                      finally { setActionBusy(null); }
                    }}
                    disabled={busy}
                    data-testid={`button-request-viren-mission-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs text-[#FF5C00] hover:text-[#FF5C00]/80 font-medium disabled:opacity-50"
                  >{busy ? "Sending" : "Request this mission"} <ArrowRight className="w-3 h-3" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between gap-3 text-[10px] text-white/60">
        <span>{data.inputsUsed || ""}</span>
        {generatedWhen && <span className="shrink-0">Updated {generatedWhen}</span>}
      </div>
    </div>
  );
}
